import {
  Button,
  Card,
  CardHeader,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { some } from "lodash";
import React from "react";
import { useAlert } from "react-alert";

import { useDispatch, useSelector } from "react-redux";

import {
  AddIcon,
  EditIcon,
  SettingsIcon,
} from "../components/icons/themes/windows10";

import Navigation from "../Navigation";
import { makeRequest } from "../util";
import { AdvancedConfigProjectModal } from "./AdvancedConfigProjectModal";
import { EditProjectModal, ModalState } from "./EditProjectModal";
import {
  EditableEnvironment,
  EditableProject,
  EnvironmentsState,
  LoadedProject,
} from "../reducers/environments";
import { fetchAccountMetadata } from "../actions/environments";

const EnvironmentChip: React.FC<{
  name: string;
}> = ({ name }) => {
  return <Chip style={{ marginRight: 8 }} color="primary" label={name} />;
};

const ProjectsPage = () => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const accountStore: EnvironmentsState = useSelector((state) => {
    // @ts-ignore
    return state.environments;
  });
  const { accountMetadata, loaded } = accountStore;
  const { projects } = accountMetadata;
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const [settingsModalOpen, setSettingsModalOpen] = React.useState<boolean>(
    false
  );
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [modalState, setModalState] = React.useState<ModalState | null>(null);
  const [
    settingsModalState,
    setSettingsModalState,
  ] = React.useState<ModalState | null>(null);

  const loadAccountData = React.useCallback(
    (force = false) => {
      if (loaded && !force) {
        return;
      }
      dispatch(fetchAccountMetadata());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  React.useEffect(() => {
    loadAccountData();
  }, [loadAccountData]);

  const closeModal = () => {
    setErrorMessage("");
    setModalState(null);
    setSettingsModalState(null);
    setModalOpen(false);
    setSettingsModalOpen(false);
  };

  const saveProject = (errorMessage: string) => (project: EditableProject) => {
    if (!project.name) {
      setErrorMessage("You must set a project name.");
      return;
    }

    if (some(project.environments.map((e: EditableEnvironment) => !e.name))) {
      setErrorMessage("Every environment must have a name.");
      return;
    }

    makeRequest<any>("save_project", project)
      .then((response) => {
        loadAccountData(true);
        closeModal();
        alert.success("Project Saved");
      })
      .catch(() => {
        setErrorMessage(errorMessage);
      });
  };

  const saveNewProject = saveProject("Failed to create new project.");
  const updateProject = saveProject("Failed to update project.");

  const openCreateNewProjectModal = () => {
    const proj: EditableProject = {
      ID: null,
      name: "",
      environments: [
        { name: "Production", ID: null, projectID: null, deletable: true },
        { name: "Development", ID: null, projectID: null, deletable: true },
      ],
    };
    setModalState({
      title: "New Project",
      placeholder: "Project",
      closeOnOverlay: false,
      addingNewProject: true,
      project: proj,
      onClose: closeModal,
      onSave: (newProjectState) => {
        saveNewProject(newProjectState);
      },
    });
    setModalOpen(true);
  };

  const openUpdateProjectModal = (
    project: EditableProject,
    addNewEnv = false
  ) => {
    setModalState({
      title: "Edit Project",
      placeholder: "Project",
      closeOnOverlay: false,
      addingNewEnvironment: addNewEnv,
      project,
      onClose: closeModal,
      onSave: (newProjectState) => {
        updateProject(newProjectState);
      },
    });
    setModalOpen(true);
  };

  const openProjectSettingsModal = (
    project: EditableProject,
    addNewEnv = false
  ) => {
    setSettingsModalState({
      title: "Advanced Settings",
      placeholder: "Project",
      closeOnOverlay: false,
      addingNewEnvironment: addNewEnv,
      project,
      onClose: closeModal,
      onSave: (newProjectState) => {
        updateProject(newProjectState);
      },
    });
    setSettingsModalOpen(true);
  };

  return (
    <>
      {modalOpen && modalState && (
        <EditProjectModal {...modalState} error={errorMessage} />
      )}
      {settingsModalOpen && settingsModalState && (
        <AdvancedConfigProjectModal
          {...settingsModalState}
          error={errorMessage}
        />
      )}
      <Card className="overflow-x-visible my-4" style={{ overflow: "visible" }}>
        <CardHeader
          title={
            <div className="flex items-center justify-between">
              <div className="pr-4">Projects</div>
              <Button
                className="mx-4"
                color="primary"
                onClick={() => openCreateNewProjectModal()}
                variant="contained"
              >
                <Add /> Add Project
              </Button>
            </div>
          }
        />
        <Divider />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className="w-40">Name</TableCell>
              <TableCell>Environments</TableCell>
              <TableCell align="center" className="w-32">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((project) => (
              <TableRow key={project.ID} className="group hover:bg-gray-100">
                <TableCell>
                  <div className="text-gray-600 font-bold text-sm truncate">
                    {project.name}
                  </div>
                </TableCell>
                <TableCell>
                  {!!project.environments.length &&
                    project.environments.map((environment) => (
                      <EnvironmentChip
                        key={environment.ID}
                        name={environment.name}
                      />
                    ))}
                  {!!project.environments.length && (
                    <Chip
                      icon={<AddIcon className="text-2xl" />}
                      label="Add Environment"
                      variant="outlined"
                      className="invisible group-hover:visible"
                      onClick={() => openUpdateProjectModal(project, true)}
                    />
                  )}
                  {!project.environments.length && (
                    <Chip
                      icon={<AddIcon className="text-2xl" />}
                      label="Add Environment"
                      variant="outlined"
                      onClick={() => openUpdateProjectModal(project, true)}
                    />
                  )}
                </TableCell>
                <TableCell align="center">
                  <div className="flex">
                    <div
                      className="mr-1 bg-white rounded-full hover:bg-purple-400 fill-gray-700 hover:text-white group cursor-pointer flex justify-center items-center w-8 h-8"
                      onClick={() => openUpdateProjectModal(project)}
                    >
                      <EditIcon />
                    </div>
                    <div
                      className="ml-1 bg-white rounded-full hover:bg-purple-400 fill-gray-700 hover:text-white group cursor-pointer flex justify-center items-center w-8 h-8"
                      onClick={() => openProjectSettingsModal(project)}
                    >
                      <SettingsIcon />
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </>
  );
};

function ProjectsPageWithNavigation() {
  return (
    <Navigation>
      <ProjectsPage />
    </Navigation>
  );
}

export default ProjectsPageWithNavigation;
